import {Text} from '@mantine/core';
import React from 'react';

import {assert} from 'Asserts';
import {pluralize} from 'BenchlingUtil';
import {ButtonStyle} from 'ui-library/buttons/Button';
import Modal from 'ui-library/modals/Modal';

interface UpdateRunDesignConfirmationModalProps {
  conditionReplicateCount: number;
  activeMethodName: string | null;
  downstreamUnitOperationCount: number | null;
  onRequestClose: () => void;
  onRequestSubmit: () => void;
}

function renderUpdateRunDesignConfirmationText({
  conditionReplicateCount,
  activeMethodName,
  downstreamUnitOperationCount,
}: {
  conditionReplicateCount: number;
  activeMethodName: string | null;
  downstreamUnitOperationCount: number | null;
}): JSX.Element {
  const conditionReplicateText = `${conditionReplicateCount} ${pluralize(
    conditionReplicateCount,
    'condition replicate'
  )}`;
  const addingConditionReplicateText = `Adding ${conditionReplicateText}`;
  // We only allow editing the run design with a null active method when split-pool is disabled
  // In this case, we're adding the condition-replicates to all methods and we don't need to specify which ones
  if (activeMethodName == null) {
    return (
      <Text size="md">
        <Text span={true} fw="bold">
          {addingConditionReplicateText}
        </Text>
      </Text>
    );
  }

  assert(
    downstreamUnitOperationCount != null,
    'Downstream unit operation count should not be null when active method is not null'
  );
  const downstreamUnitOperationText = `${downstreamUnitOperationCount} ${pluralize(
    downstreamUnitOperationCount,
    'downstream unit operation'
  )}`;
  const shouldIncludeDownstreamUnitOperations = downstreamUnitOperationCount > 0;
  // TODO (BNCH-158320): Include removed unit ops
  return (
    <Text size="md">
      <Text span={true} fw="bold">
        {addingConditionReplicateText}
      </Text>{' '}
      at{' '}
      <Text span={true} fw="bold">
        {activeMethodName}
      </Text>
      {shouldIncludeDownstreamUnitOperations && <> and {downstreamUnitOperationText}</>}
    </Text>
  );
}

export default function UpdateRunDesignConfirmationModal({
  conditionReplicateCount,
  activeMethodName,
  downstreamUnitOperationCount,
  onRequestClose,
  onRequestSubmit,
}: UpdateRunDesignConfirmationModalProps): JSX.Element {
  return (
    <Modal
      dataTestAttrs={{component: 'UpdateRunDesignConfirmationModal', element: 'root'}}
      title="Update run design?"
      buttons={[
        {
          text: 'Cancel',
          cancel: true,
          style: ButtonStyle.TERTIARY_GRAY,
        },
        {
          text: 'Update',
          submit: true,
          style: ButtonStyle.PRIMARY,
          dataTestAttrs: {component: 'UpdateRunDesignConfirmationModal', element: 'updateButton'},
        },
      ]}
      onRequestClose={onRequestClose}
      onRequestSubmit={onRequestSubmit}
    >
      {renderUpdateRunDesignConfirmationText({
        conditionReplicateCount,
        activeMethodName,
        downstreamUnitOperationCount,
      })}
    </Modal>
  );
}
